import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import unifiedApi from '../../../utils/unifiedApi';

const WaiverConfigSection = ({ canEdit = false }) => {
  const [waiverText, setWaiverText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchWaiver = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await unifiedApi.getWaiverText();
      setWaiverText(data?.waiverText || '');
    } catch {
      setMessage({ type: 'error', text: 'Failed to load waiver text.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWaiver();
  }, [fetchWaiver]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await unifiedApi.updateWaiverText(waiverText);
      setMessage({ type: 'success', text: 'Waiver text updated successfully.' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to update waiver text.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Registration Waiver
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This waiver text is displayed to all registrants during the registration process.
        They must accept it before completing their registration.
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <TextField
        label="Waiver Text"
        multiline
        minRows={6}
        maxRows={20}
        fullWidth
        value={waiverText}
        onChange={(e) => setWaiverText(e.target.value)}
        disabled={!canEdit || saving}
        placeholder="Enter the waiver/liability agreement text that registrants must accept..."
        sx={{ mb: 2 }}
      />

      {canEdit && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Waiver'}
        </Button>
      </Box>
      )}
    </Paper>
  );
};

export default WaiverConfigSection;
